import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { IoAddCircleOutline, IoTrashOutline, IoPencilOutline } from "react-icons/io5";

const ProfilePage = () => {
    const [points, setPoints] = useState([]);
    const [newPoint, setNewPoint] = useState("");
    const [editIndex, setEditIndex] = useState(null);
    const [editValue, setEditValue] = useState("");

    useEffect(() => {
        const storedPoints = localStorage.getItem("profilePoints");
        if (storedPoints) {
            setPoints(JSON.parse(storedPoints));
        }
    }, []);

    const handleAddPoint = () => {
        if (newPoint.trim()) {
            const updatedPoints = [...points, newPoint.trim()];
            setPoints(updatedPoints);
            localStorage.setItem("profilePoints", JSON.stringify(updatedPoints));
            setNewPoint("");
        }
    };

    const handleDeletePoint = (index) => {
        const updatedPoints = points.filter((_, i) => i !== index);
        setPoints(updatedPoints);
        localStorage.setItem("profilePoints", JSON.stringify(updatedPoints));
    };

    const handleEditPoint = (index) => {
        setEditIndex(index);
        setEditValue(points[index]);
    };

    const handleSaveEdit = () => {
        const updatedPoints = [...points];
        updatedPoints[editIndex] = editValue.trim();
        setPoints(updatedPoints);
        localStorage.setItem("profilePoints", JSON.stringify(updatedPoints));
        setEditIndex(null);
        setEditValue("");
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background-light to-primary-50 dark:from-background-dark dark:to-primary-900 transition-colors duration-300 p-6">
            <div className="container mx-auto max-w-4xl">
                <motion.h1
                    className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4 text-center"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    Vineeth's Profile
                </motion.h1>

                <motion.h2
                    className="text-lg text-gray-700 dark:text-gray-300 mb-6 text-center"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    Agent Learnings About You
                </motion.h2>
                <p className="text-base text-gray-800 dark:text-gray-400 mb-8 text-center">
                    This section contains key points that the agent has learned about you. You can manage these points by adding, editing, or deleting them as needed.
                </p>

                <div className="flex gap-4 mb-6">
                    <label htmlFor="new-point-input" className="sr-only">
                        Add a new point
                    </label>
                    <input
                        id="new-point-input"
                        type="text"
                        className="flex-grow p-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Add a new point"
                        value={newPoint}
                        onChange={(e) => setNewPoint(e.target.value)}
                    />
                    <button
                        onClick={handleAddPoint}
                        className="px-4 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
                        aria-label="Add new point"
                    >
                        <IoAddCircleOutline size={24} />
                    </button>
                </div>

                <ul className="space-y-4">
                    {points.map((point, index) => (
                        <li
                            key={index}
                            className="flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-lg shadow-md border border-gray-200 dark:border-gray-700"
                        >
                            {editIndex === index ? (
                                <>
                                    <label htmlFor={`edit-point-input-${index}`} className="sr-only">
                                        Edit point
                                    </label>
                                    <input
                                        id={`edit-point-input-${index}`}
                                        type="text"
                                        className="flex-grow p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                    />
                                </>
                            ) : (
                                <span className="text-gray-900 dark:text-gray-100">
                                    {point}
                                </span>
                            )}

                            <div className="flex items-center gap-2">
                                {editIndex === index ? (
                                    <button
                                        onClick={handleSaveEdit}
                                        className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition"
                                        aria-label="Save changes"
                                    >
                                        Save
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleEditPoint(index)}
                                        className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition"
                                        aria-label={`Edit point: ${point}`}
                                    >
                                        <IoPencilOutline size={20} />
                                    </button>
                                )}
                                <button
                                    onClick={() => handleDeletePoint(index)}
                                    className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition"
                                    aria-label={`Delete point: ${point}`}
                                >
                                    <IoTrashOutline size={20} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default ProfilePage;